import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Download as DownloadIcon,
  Inventory as InventoryIcon 
} from '@mui/icons-material';
import { warehouseItemService, installationItemService, teamService } from '../services/api';
import * as XLSX from 'xlsx';

export default function WarehouseItems() {
  const [warehouseItems, setWarehouseItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [giveToTeamDialogOpen, setGiveToTeamDialogOpen] = useState(false);
  const [exportTeamDialogOpen, setExportTeamDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedWarehouseItem, setSelectedWarehouseItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({
    item: '',
    quantity: 0,
  });
  const [giveToTeamData, setGiveToTeamData] = useState({
    team: '',
    quantity: '',
    notes: '',
  });
  const [selectedTeamForExport, setSelectedTeamForExport] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [warehouseResponse, itemsResponse, teamsResponse] = await Promise.all([
        warehouseItemService.getWarehouseItems(),
        installationItemService.getItems(),
        teamService.getTeams(),
      ]);
      setWarehouseItems(warehouseResponse.data);
      setAllItems(itemsResponse.data.filter(item => item.is_active));
      setTeams(teamsResponse.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (warehouseItem = null) => {
    if (warehouseItem) {
      setEditMode(true);
      setSelectedWarehouseItem(warehouseItem);
      setFormData({
        item: warehouseItem.item._id,
        quantity: warehouseItem.quantity,
      });
    } else {
      setEditMode(false);
      setSelectedWarehouseItem(null);
      setFormData({
        item: '',
        quantity: 0,
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleSave = async () => {
    if (!formData.item || formData.quantity < 0) {
      setError('Item and quantity (>= 0) are required');
      return;
    }

    try {
      if (editMode) {
        await warehouseItemService.updateWarehouseItem(selectedWarehouseItem._id, {
          quantity: formData.quantity,
        });
        setSuccess('Warehouse item updated successfully');
      } else {
        await warehouseItemService.createWarehouseItem(formData);
        setSuccess('Warehouse item created successfully');
      }
      setDialogOpen(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = (warehouseItem) => {
    setItemToDelete(warehouseItem);
    setDeleteDialogOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    try {
      await warehouseItemService.deleteWarehouseItem(itemToDelete._id);
      setSuccess(`Warehouse item deleted successfully`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpenGiveToTeam = (warehouseItem) => {
    setSelectedWarehouseItem(warehouseItem);
    setGiveToTeamData({
      team: '',
      quantity: '',
      notes: '',
    });
    setGiveToTeamDialogOpen(true);
    setError('');
  };

  const handleGiveToTeam = async () => {
    if (!giveToTeamData.team || !giveToTeamData.quantity || parseFloat(giveToTeamData.quantity) <= 0) {
      setError('Team and valid quantity are required');
      return;
    }

    const quantity = parseFloat(giveToTeamData.quantity);
    if (quantity > selectedWarehouseItem.quantity) {
      setError(`Insufficient quantity. Available: ${selectedWarehouseItem.quantity}`);
      return;
    }

    try {
      await warehouseItemService.giveToTeam(selectedWarehouseItem._id, {
        team: giveToTeamData.team,
        quantity: quantity,
        notes: giveToTeamData.notes || undefined,
      });
      setSuccess(`Successfully assigned ${quantity} to team`);
      setGiveToTeamDialogOpen(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  /**
   * Export warehouse items to Excel
   */
  const handleExportWarehouse = async () => {
    setExporting(true);
    
    try {
      const excelData = warehouseItems.map((wi) => ({
        'Item Name': wi.item?.name || '',
        'Description': wi.item?.description || '',
        'Unit': wi.item?.unit || '',
        'Available Quantity': wi.quantity || 0,
        'Installation Type': wi.item?.installation_type || '',
        'Created At': new Date(wi.createdAt).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        }),
        'Updated At': new Date(wi.updatedAt).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        }),
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      const colWidths = [
        { wch: 25 }, // Item Name
        { wch: 30 }, // Description
        { wch: 15 }, // Unit
        { wch: 18 }, // Available Quantity
        { wch: 18 }, // Installation Type
        { wch: 15 }, // Created At
        { wch: 15 }, // Updated At
      ];

      ws['!cols'] = colWidths;
      XLSX.utils.book_append_sheet(wb, ws, 'Warehouse Items');

      const fileName = `warehouse_items_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      alert('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  /**
   * Export team assignments to Excel
   */
  const handleExportTeamAssignments = async () => {
    if (!selectedTeamForExport) {
      setError('Please select a team');
      return;
    }

    setExporting(true);
    
    try {
      const response = await warehouseItemService.getTeamAssignments(selectedTeamForExport);
      const assignments = response.data;

      if (assignments.length === 0) {
        alert('No assignments found for this team');
        setExporting(false);
        return;
      }

      // Format: team-1 17/12/2025 15:15 25 piece item-123
      const excelData = assignments.map((assignment) => {
        const date = new Date(assignment.createdAt);
        const dateStr = date.toLocaleDateString('en-GB', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        });
        const timeStr = date.toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false
        });
        
        return {
          'Team': assignment.team?.name || '',
          'Date': dateStr,
          'Time': timeStr,
          'Quantity': assignment.quantity || 0,
          'Unit': assignment.item?.unit || 'piece',
          'Item Name': assignment.item?.name || '',
          'Assigned By': assignment.assigned_by?.full_name || assignment.assigned_by?.username || '',
          'Notes': assignment.notes || '',
          'Full Record': `${assignment.team?.name || 'team'} ${dateStr} ${timeStr} ${assignment.quantity} ${assignment.item?.unit || 'piece'} ${assignment.item?.name || ''}`,
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      const colWidths = [
        { wch: 20 }, // Team
        { wch: 12 }, // Date
        { wch: 10 }, // Time
        { wch: 12 }, // Quantity
        { wch: 12 }, // Unit
        { wch: 25 }, // Item Name
        { wch: 20 }, // Assigned By
        { wch: 30 }, // Notes
        { wch: 60 }, // Full Record
      ];

      ws['!cols'] = colWidths;
      
      const teamName = teams.find(t => t._id === selectedTeamForExport)?.name || 'team';
      XLSX.utils.book_append_sheet(wb, ws, `${teamName} Assignments`);

      const fileName = `team_assignments_${teamName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      alert('Excel file downloaded successfully!');
      setExportTeamDialogOpen(false);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const filteredWarehouseItems = warehouseItems.filter((wi) =>
    wi.item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wi.item?.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      field: 'itemName',
      headerName: 'Item Name',
      width: 250,
      valueGetter: (params) => params.row.item?.name || 'N/A',
      renderCell: (params) => <strong>{params.value}</strong>,
    },
    {
      field: 'unit',
      headerName: 'Unit',
      width: 100,
      valueGetter: (params) => params.row.item?.unit || 'piece',
    },
    {
      field: 'quantity',
      headerName: 'Available Quantity',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value > 0 ? 'success' : 'error'}
          size='small'
        />
      ),
    },
    {
      field: 'installation_type',
      headerName: 'Installation Type',
      width: 150,
      valueGetter: (params) => params.row.item?.installation_type || 'all',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 280,
      renderCell: (params) => (
        <Box>
          <Button
            variant='outlined'
            size='small'
            onClick={() => handleOpenDialog(params.row)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant='outlined'
            size='small'
            color='primary'
            startIcon={<InventoryIcon />}
            onClick={() => handleOpenGiveToTeam(params.row)}
            sx={{ mr: 1 }}
            disabled={params.row.quantity <= 0}
          >
            Give to Team
          </Button>
          <Button
            variant='outlined'
            size='small'
            color='error'
            startIcon={<DeleteIcon />}
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h4'>Warehouse Items</Typography>
        <Box>
          <Button
            variant='contained'
            color='info'
            startIcon={exporting ? <CircularProgress size={20} color='inherit' /> : <DownloadIcon />}
            onClick={() => setExportTeamDialogOpen(true)}
            disabled={exporting}
            sx={{ mr: 2 }}
          >
            Export Team Assignments
          </Button>
          <Button
            variant='contained'
            color='success'
            startIcon={exporting ? <CircularProgress size={20} color='inherit' /> : <DownloadIcon />}
            onClick={handleExportWarehouse}
            disabled={exporting || warehouseItems.length === 0}
            sx={{ mr: 2 }}
          >
            {exporting ? 'Exporting...' : 'Export Warehouse'}
          </Button>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {error && !dialogOpen && !giveToTeamDialogOpen && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TextField
            label='Search'
            placeholder='Search by item name or description...'
            variant='outlined'
            fullWidth
            margin='normal'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
          />
          <DataGrid
            rows={filteredWarehouseItems}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      {/* Create/Edit Warehouse Item Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{editMode ? 'Edit Warehouse Item' : 'Add Item to Warehouse'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel>Item</InputLabel>
            <Select
              value={formData.item}
              label='Item'
              onChange={(e) => setFormData({ ...formData, item: e.target.value })}
              disabled={editMode}
            >
              <MenuItem value=''>
                <em>-- Select an Item --</em>
              </MenuItem>
              {allItems.map((item) => (
                <MenuItem key={item._id} value={item._id}>
                  {item.name} ({item.unit})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin='dense'
            label='Quantity'
            type='number'
            fullWidth
            variant='outlined'
            value={formData.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
            inputProps={{ min: 0, step: 1 }}
            required
            helperText={editMode ? 'Update the available quantity' : 'Enter initial quantity'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant='contained'>
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Give to Team Dialog */}
      <Dialog open={giveToTeamDialogOpen} onClose={() => setGiveToTeamDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>
          Give Items to Team - {selectedWarehouseItem?.item?.name}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Alert severity='info' sx={{ mb: 2 }}>
            Available Quantity: <strong>{selectedWarehouseItem?.quantity}</strong> {selectedWarehouseItem?.item?.unit}
          </Alert>

          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel>Team</InputLabel>
            <Select
              value={giveToTeamData.team}
              label='Team'
              onChange={(e) => setGiveToTeamData({ ...giveToTeamData, team: e.target.value })}
            >
              <MenuItem value=''>
                <em>-- Select a Team --</em>
              </MenuItem>
              {teams.map((team) => (
                <MenuItem key={team._id} value={team._id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin='dense'
            label='Quantity'
            type='number'
            fullWidth
            variant='outlined'
            value={giveToTeamData.quantity}
            onChange={(e) => setGiveToTeamData({ ...giveToTeamData, quantity: e.target.value })}
            inputProps={{ min: 1, max: selectedWarehouseItem?.quantity, step: 1 }}
            required
            helperText={`Maximum: ${selectedWarehouseItem?.quantity} ${selectedWarehouseItem?.item?.unit}`}
            sx={{ mb: 2 }}
          />

          <TextField
            margin='dense'
            label='Notes (Optional)'
            type='text'
            fullWidth
            variant='outlined'
            multiline
            rows={2}
            value={giveToTeamData.notes}
            onChange={(e) => setGiveToTeamData({ ...giveToTeamData, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGiveToTeamDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleGiveToTeam} variant='contained' color='primary'>
            Assign to Team
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Team Assignments Dialog */}
      <Dialog open={exportTeamDialogOpen} onClose={() => setExportTeamDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Export Team Assignments</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel>Select Team</InputLabel>
            <Select
              value={selectedTeamForExport}
              label='Select Team'
              onChange={(e) => setSelectedTeamForExport(e.target.value)}
            >
              <MenuItem value=''>
                <em>-- Select a Team --</em>
              </MenuItem>
              {teams.map((team) => (
                <MenuItem key={team._id} value={team._id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity='info' sx={{ mt: 2 }}>
            This will export all item assignments for the selected team with dates and quantities.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportTeamDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleExportTeamAssignments} 
            variant='contained' 
            color='success'
            disabled={!selectedTeamForExport || exporting}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Warehouse Item Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Warehouse Item</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography>
            Are you sure you want to delete <strong>"{itemToDelete?.item?.name}"</strong> from warehouse?
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            This will remove the item from warehouse inventory. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            setError('');
          }}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant='contained' color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

